// NB: Everything in here is unique to the Contagion Platform application
import { config } from "@react-spring/core";
import {
  accessors,
  filterInArray,
  filterExcludeFromArray,
  lambdaConstants,
  ALL_COUNTRIES,
  ALL_BLUE_EVENTS,
  ALL_YELLOW_EVENTS,
  ALL_PURPLE_EVENTS,
  ALL_RED_EVENTS,
  ALL_GREEN_EVENTS,
  ALL_WHITE_LINES,
  CHART_DIMENSIONS,
} from "../utils";

// This is hardcoded and should be the "Suspected Hack: ... data point"
const ID_OF_FIRST_EXPOSURE = 138;

// we want this because we want the animation to hit
// once we properly enter the scene
const INITIAL_DELAY = 0; // in seconds

// duration is in seconds
// const unfoldChronologically = (duration) => (d) =>
//   duration * ((d.x * 0.5) / CHART_DIMENSIONS.X.MAX);
const unfoldChronologically =
  (duration, initialDelay = 0) =>
  (d) =>
    initialDelay + duration * (d.x / CHART_DIMENSIONS.X.MAX);

const scene = {
  title: "",
  subtitle: "",
  text: "",
  data: [],
  filters: [() => true],
  media: {
    type: null,
    src: null,
    opacity: 0.4, // not needed yet
  },
  // all these props are drilled into <Timeline />
  timeline: {
    // this object is passed to our material's shaders
    material: {
      isBreathing: false,
      isPulsating: false,
    },
    bloomIntensity: 0.5,
    resetOrbitControls: true,
    rotateCarpetAssociationText: [0, 0, 0],
    textAssociation: {
      anchorX: "right",
      x: -1,
    },
    translateX: 0,
    translateY: -CHART_DIMENSIONS.Y.MAX / 4,
    translateYCarpetView: 0,
    rotation: [0, 0, 0],
    scaleTimelineWidth: 1,
    showGridLinesTargets: false,
    showGridLinesFloorX: false,
    showGridLinesFloorZ: false,
    showGlobal: false,
    showDates: false,
    showTargets: false,
    showTargetNames: false,
    springTransitionConfig: { ...config.default, duration: 2000 },
    topView: false,
    zoomOnMesh: 1,
    zoomToData: true,
  },
  // the configuration for creating attributes
  attributeModifiers: [],
  // this is passed to our useTransitionAttributeArray hook
  transitionConfig: {},
};

export function makeNarrative({ data }) {
  return [
    {
      ...scene,
      // data,
      filters: [() => true],
      title:
        '<span class="bold uppercase">Digital Violence</span> <span class="">Platform</span>',
      attributeModifiers: [["opacity", lambdaConstants.zero]],
    },
    {
      ...scene,
      text: "The Digital Violence Platform is an extensive database documenting NSO Group's operations.",
      timeline: {
        ...scene.timeline,
        material: {
          isBreathing: false,
        },
        showGridLinesTargets: true,
        showGridLinesFloorX: true,
        showGridLinesFloorZ: true,
        rotation: [0, Math.PI / 4, 0],
        translateX: 0,
        zoomOnMesh: 1.2,
      },
      attributeModifiers: [["opacity", lambdaConstants.zero]],
    },
    {
      ...scene,
      text: "It plots every digital infection in time",
      // data,
      filters: [
        filterInArray(ALL_BLUE_EVENTS, accessors.event.type),
        filterInArray(["Mexico"], accessors.association),
        filterInArray(["targeted"], accessors.attribute.type),
        filterExcludeFromArray(["Legal Team"], accessors.target.name),
      ],
      timeline: {
        ...scene.timeline,
        bloomIntensity: 1,
        material: {
          isPulsating: false,
        },
        showGridLinesTargets: true,
        zoomToData: true,
        zoomOnMesh: 4,
        translateY: -CHART_DIMENSIONS.Y.MAX * 0.46,
        translateX: CHART_DIMENSIONS.X.MAX * 0.102,
        rotation: [0, 0, 0],
        // rotation: [0, Math.PI / 4, 0],
      },
      attributeModifiers: [
        ["opacity", (d) => (accessors.id(d) === ID_OF_FIRST_EXPOSURE ? 1 : 0)],
      ],
      transitionConfig: {
        delayFunc: (d) =>
          INITIAL_DELAY +
          (accessors.id(d) === ID_OF_FIRST_EXPOSURE
            ? 1
            : 2 + d.x / CHART_DIMENSIONS.X.MAX),
        // delayFunc: unfoldChronologically(2),
        duration: 1,
      },
    },
    {
      ...scene,
      text: "according to the targeted individuals.",
      bloomIntensity: 1,
      filters: [
        filterInArray(ALL_BLUE_EVENTS, accessors.event.type),
        filterInArray(["Mexico"], accessors.association),
        filterInArray(["targeted"], accessors.attribute.type),
        filterExcludeFromArray(["Legal Team"], accessors.target.name),
      ],
      timeline: {
        ...scene.timeline,
        material: {
          isPulsating: false,
        },
        showGridLinesTargets: true,
        zoomToData: true,
        showTargetNames: true,
        showTargets: true,
        zoomOnMesh: 0.25,
        translateX: CHART_DIMENSIONS.X.MAX * 0.15,
        translateY: -CHART_DIMENSIONS.Y.MAX / 2,
        translateMeshOnly: [-CHART_DIMENSIONS.X.MAX * 0.2, 0, 0],
        rotation: [0, 0, 0],
        // rotation: [0, Math.PI / 4, 0],
      },
      attributeModifiers: [
        ["x", (d) => d.x - 5],
        [
          "opacity",
          (d) =>
            accessors.id(d) === ID_OF_FIRST_EXPOSURE ? 1 : accessors.opacity(d),
        ],
      ],
      transitionConfig: {
        delayFunc: (d) =>
          accessors.id(d) === ID_OF_FIRST_EXPOSURE
            ? 1
            : 2 + d.x / CHART_DIMENSIONS.X.MAX,
        // delayFunc: unfoldChronologically(2),
        duration: 1,
      },
    },
    {
      ...scene,
      text: "And according to the states that operated surveillance.",
      // data,
      filters: [
        filterInArray(ALL_BLUE_EVENTS, accessors.event.type),
        filterInArray(ALL_COUNTRIES, accessors.association),
      ],
      transitionConfig: {
        delayFunc: (d) => {
          // const order = d.z / CHART_DIMENSIONS.Z.MAX;
          return INITIAL_DELAY + d.z / 20 + (d.x / CHART_DIMENSIONS.X.MAX) * 2;
        },
        duration: 2,
      },
      timeline: {
        ...scene.timeline,
        axisTransitionConfig: {
          grid: {
            delay: (x, y, z) => INITIAL_DELAY + 1000,
          },
          associations: {
            delay: (x, y, z) => INITIAL_DELAY + (z / 20 + 1) * 1000,
          },
          targets: { delay: (d) => 0 },
        },
        textAssociation: {
          x: 1,
          anchorX: "left",
        },
        translateX: CHART_DIMENSIONS.X.MAX * 0.1,
        translateY: -CHART_DIMENSIONS.X.MAX / 10,
        rotation: [0, Math.PI * 0.3, 0],
        rotateCarpetAssociationText: [0, 0, 0],
        showDates: false,
        showTargetNames: false,
        showTargets: true,
        showGridLinesTargets: false,
        showGridLinesFloorX: true,
        showGridLinesFloorZ: true,
        zoomToData: true,
        zoomOnMesh: 1,
      },
    },
    {
      ...scene,
      text: "It records related incidents of violence experienced in the physical world",
      filters: [
        filterInArray(
          [...ALL_BLUE_EVENTS, ...ALL_RED_EVENTS],
          accessors.event.type
        ),
        filterInArray(ALL_COUNTRIES, accessors.association),
      ],
      transitionConfig: {
        delayFunc: (d) =>
          ALL_RED_EVENTS.indexOf(accessors.event.type(d)) >= 0
            ? 2 + (d.x / CHART_DIMENSIONS.X.MAX) * 1.5
            : d.x / CHART_DIMENSIONS.X.MAX,
        // delayFunc: unfoldChronologically(2),
        duration: 1,
      },
      timeline: {
        ...scene.timeline,
        material: {
          isPulsating: false,
        },
        textAssociation: {
          x: 1,
          anchorX: "left",
        },
        showGridLinesTargets: false,
        zoomOnMesh: 1.4,
      },
      attributeModifiers: [
        [
          "opacity",
          (d) =>
            ALL_BLUE_EVENTS.indexOf(accessors.event.type(d)) >= 0
              ? d.opacity * 0.1
              : d.opacity,
        ],
      ],
    },
    {
      ...scene,
      text: "and by those confronting NSO—lawyers, journalists and investigators.",
      // data,
      timeline: {
        ...scene.timeline,
        translateX: -CHART_DIMENSIONS.X.MAX * 0.12,
        translateY: -CHART_DIMENSIONS.Y.MAX * 0.055,
        rotation: [0, Math.PI / 4, 0],
        showGridLinesTargets: false,
        showTargets: false,
        showTargetNames: false,
        showDates: false,
        showGlobal: false,
        zoomToData: true,
        zoomOnMesh: 4,
      },
      filters: [
        filterInArray(
          [...ALL_BLUE_EVENTS, ...ALL_RED_EVENTS, ...ALL_YELLOW_EVENTS],
          accessors.event.type
        ),
        // (d) => {
        //   if (
        //     d.type === "global" &&
        //     [...ALL_YELLOW_EVENTS].indexOf(accessors.event.type(d)) < 0
        //   )
        //     return false;

        //   return true;
        // },
      ],
      transitionConfig: {
        ...scene.transitionConfig,
        delayFunc: (d) =>
          [...ALL_YELLOW_EVENTS].indexOf(accessors.event.type(d)) >= 0
            ? 1
            : 0.5,
        duration: 1,
      },
      attributeModifiers: [
        [
          "opacity",
          (d) =>
            [...ALL_YELLOW_EVENTS].indexOf(accessors.event.type(d)) >= 0
              ? d.opacity
              : [...ALL_RED_EVENTS].indexOf(accessors.event.type(d)) >= 0
              ? d.opacity * 0.15 // override red for visual balance
              : d.opacity * 0.07,
        ],
      ],
    },
    {
      ...scene,
      text: "It reveals how digital infections interrelate with political events.",
      // data,
      filters: [
        filterInArray(
          [
            ...ALL_WHITE_LINES,
            ...ALL_BLUE_EVENTS,
            ...ALL_RED_EVENTS,
            ...ALL_YELLOW_EVENTS,
          ],
          accessors.event.type
        ),
      ],
      // TODO Even out visually the reds
      attributeModifiers: [
        [
          "opacity",
          (d) =>
            [...ALL_WHITE_LINES].indexOf(accessors.event.type(d)) >= 0
              ? d.opacity * 0.8
              : ALL_RED_EVENTS.indexOf(accessors.event.type(d)) >= 0
              ? d.opacity * 0.2 // override red for visual balance
              : d.opacity * 0.05,
        ],
      ],
      transitionConfig: {
        delayFunc: (d) =>
          [...ALL_WHITE_LINES].indexOf(accessors.event.type(d)) >= 0
            ? 0.5 + INITIAL_DELAY + (d.x / CHART_DIMENSIONS.X.MAX) * 2
            : 0,
        // delayFunc: unfoldChronologically(2, INITIAL_DELAY),
        duration: 1,
      },
      timeline: {
        ...scene.timeline,
        // rotation: [Math.PI / 4, 0, 0],
        showGridLinesTargets: false,
        showDates: false,
        showGlobal: false,
        zoomToData: true,
        zoomOnMesh: 1.2,
      },
    },
    {
      ...scene,
      text: "And how private investment helps proliferate global surveillance.",
      // data,
      // filter global events to show only
      // green ones
      filters: [
        filterInArray(
          [
            ...ALL_WHITE_LINES,
            ...ALL_BLUE_EVENTS,
            ...ALL_RED_EVENTS,
            ...ALL_YELLOW_EVENTS,
            ...ALL_GREEN_EVENTS,
          ],
          accessors.event.type
        ),
        (d) => {
          if (
            d.type === "global" &&
            [...ALL_GREEN_EVENTS, ...ALL_YELLOW_EVENTS].indexOf(
              accessors.event.type(d)
            ) < 0
          )
            return false;

          return true;
        },
      ],
      transitionConfig: {
        delayFunc: unfoldChronologically(1),
        duration: 1,
      },
      timeline: {
        ...scene.timeline,
        material: {
          depthTest: false,
        },
        translateYCarpetView: CHART_DIMENSIONS.Y.MAX * 0.22,
        translateX: CHART_DIMENSIONS.X.MAX * 0.1,
        showGridLinesTargets: false,
        showTargets: true,
        showTargetNames: false,
        showGlobal: true,
        topView: true,
        zoomToData: true,
        zoomOnMesh: 0.7,
      },
    },
    {
      ...scene,
      text: "The platform also documents the work of civil society groups confronting NSO.",
      // data,
      filters: [
        filterInArray(
          [
            ...ALL_WHITE_LINES,
            ...ALL_BLUE_EVENTS,
            ...ALL_RED_EVENTS,
            ...ALL_YELLOW_EVENTS,
            ...ALL_GREEN_EVENTS,
            ...ALL_PURPLE_EVENTS,
          ],
          accessors.event.type
        ),
        (d) => {
          if (
            d.type === "global" &&
            [
              ...ALL_PURPLE_EVENTS,
              ...ALL_GREEN_EVENTS,
              ...ALL_YELLOW_EVENTS,
            ].indexOf(accessors.event.type(d)) < 0
          )
            return false;

          return true;
        },
      ],
      springTransitionConfig: {
        duration: 2000,
      },
      transitionConfig: {
        delayFunc: unfoldChronologically(1),
        duration: 1,
      },
      timeline: {
        ...scene.timeline,
        material: {
          depthTest: false,
        },
        translateYCarpetView: CHART_DIMENSIONS.Y.MAX * 0.22,
        translateX: CHART_DIMENSIONS.X.MAX * 0.1,
        rotation: [0, Math.PI / 5, 0],
        showTargets: true,
        showTargetNames: false,
        showGlobal: true,
        topView: true,
        zoomToData: true,
        zoomOnMesh: 0.7,
      },
    },
    {
      ...scene,
      text: 'The <span class="bold">Digital Violence</span> platform charts the landscape of global surveillance and repression powered by NSO Group.',
      // data,
      transitionConfig: {
        duration: 2,
      },
      timeline: {
        ...scene.timeline,
        material: {
          isBreathing: false,
          isPulsating: false,
        },
        rotateCarpetAssociationText: [-Math.PI / 4, 0, 0],
        rotation: [Math.PI / 40, 0, 0],
        translateX: CHART_DIMENSIONS.X.MAX / 16,
        translateY: -CHART_DIMENSIONS.Y.MAX / 7,
        showTargets: true,
        // adding this here, even if we're not showing axes
        // so that it updates in the background
        showTargetNames: false,
        showGlobal: false,
        showGridLinesTargets: false,
        showGridLinesFloorX: true,
        showGridLinesFloorZ: true,
        showDates: true,
        zoomToData: true,
        zoomOnMesh: 0.7,
      },
    },
  ];
}
